// Similarity-score banding shared by the star viz and the member rows. All
// colors resolve to design tokens (no hardcoded values).

export type ScoreBand = 'strong' | 'likely' | 'weak' | 'unknown'

export function scoreBand(score: number | null | undefined): ScoreBand {
  if (score == null) return 'unknown'
  if (score >= 0.95) return 'strong'
  if (score >= 0.85) return 'likely'
  return 'weak'
}

/** Badge variant per band (Badge supports success | info | warning | muted). */
export const scoreBadgeVariant: Record<ScoreBand, 'success' | 'info' | 'warning' | 'muted'> = {
  strong: 'success',
  likely: 'info',
  weak: 'warning',
  unknown: 'muted',
}

/** CSS-var token for SVG stroke/fill where Tailwind classes don't reach. */
export const scoreVar: Record<ScoreBand, string> = {
  strong: 'var(--success)',
  likely: 'var(--info)',
  weak: 'var(--warning)',
  unknown: 'var(--muted-foreground)',
}

export const scoreLabel: Record<ScoreBand, string> = {
  strong: 'Strong',
  likely: 'Likely',
  weak: 'Weak',
  unknown: 'Unscored',
}

export function scorePct(score: number | null | undefined): string {
  return score == null ? '—' : `${Math.round(score * 100)}%`
}

/** Up-to-2-letter monogram for a name, used in avatars and star nodes. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/** Character trigrams two names share (diacritic-folded) — the blocking keys that
 * made the matcher compare them. Empty for cross-script pairs. */
export function sharedTrigrams(a: string, b: string): string[] {
  const grams = (s: string) => {
    const t = s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
    const out = new Set<string>()
    for (let i = 0; i + 3 <= t.length; i++) out.add(t.slice(i, i + 3))
    return out
  }
  const gb = grams(b)
  return [...grams(a)].filter(g => gb.has(g)).slice(0, 8)
}
